import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    getInvoiceApprovalStatus,
    updateInvoiceApprovalStatus,
    submitForApproval,
} from "src/docuclipper/api";
import {
    InvoiceApprovalStatus,
    ApprovalProgress,
} from "src/docuclipper/DocuclipperTypes";

interface InvoiceApprovalState {
    loading: boolean;
    status: InvoiceApprovalStatus | null;
    isApprover: boolean;
    needsApproval: boolean;
    approvalProgress: ApprovalProgress | null;
    error: string | null;
    approvalActionLoading: boolean;
}

const initialState: InvoiceApprovalState = {
    loading: true,
    status: null,
    isApprover: false,
    needsApproval: false,
    approvalProgress: null,
    error: null,
    approvalActionLoading: false,
};

const slice = createSlice({
    name: "InvoiceApprovalStatus",
    initialState,
    reducers: {
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        },
        setApprovalActionLoading(state, action: PayloadAction<boolean>) {
            state.approvalActionLoading = action.payload;
        },
        setApprovalStatus(
            state,
            action: PayloadAction<{
                status: InvoiceApprovalStatus | null;
                isApprover: boolean;
                needsApproval: boolean;
                approvalProgress: ApprovalProgress | null;
            }>
        ) {
            state.loading = false;
            state.status = action.payload.status;
            state.isApprover = action.payload.isApprover;
            state.needsApproval = action.payload.needsApproval;
            state.approvalProgress = action.payload.approvalProgress;
            state.error = null;
        },
        setError(state, action: PayloadAction<string>) {
            state.loading = false;
            state.approvalActionLoading = false;
            state.error = action.payload;
        },
        resetState(state) {
            return { ...initialState };
        },
    },
});

export const {
    setLoading,
    setApprovalActionLoading,
    setApprovalStatus,
    setError,
    resetState,
} = slice.actions;

export const fetchApprovalStatus = (transactionLineId: string | number) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const {
            isApprover,
            approvalStatus: status,
            needsApproval,
            approvalProgress,
        } = await getInvoiceApprovalStatus(transactionLineId as any);

        dispatch(setApprovalStatus({
            status,
            isApprover,
            needsApproval,
            approvalProgress,
        }));
    } catch (error: any) {
        console.error("Error fetching approval status:", error);
        dispatch(setError(error.message || "Failed to fetch approval status"));
    }
};

export const approveInvoice = (
    transactionLineId: number,
    comment: string = ""
) => async (dispatch) => {
    try {
        dispatch(setApprovalActionLoading(true));
        const response = await updateInvoiceApprovalStatus(
            transactionLineId,
            "approved",
            comment
        );

        dispatch(setApprovalStatus({
            status: response.approvalStatus,
            isApprover: response.isApprover,
            needsApproval: response.needsApproval,
            approvalProgress: response.approvalProgress || null,
        }));
        dispatch(setApprovalActionLoading(false));

        return { success: true, message: "Invoice approved successfully" };
    } catch (error: any) {
        console.error("Error approving invoice:", error);
        dispatch(setError(error.message || "Error approving invoice"));
        throw error;
    }
};

export const rejectInvoice = (
    transactionLineId: number,
    comment: string = ""
) => async (dispatch) => {
    try {
        dispatch(setApprovalActionLoading(true));
        const response = await updateInvoiceApprovalStatus(
            transactionLineId,
            "rejected",
            comment
        );

        dispatch(setApprovalStatus({
            status: response.approvalStatus,
            isApprover: response.isApprover,
            needsApproval: response.needsApproval,
            approvalProgress: response.approvalProgress || null,
        }));
        dispatch(setApprovalActionLoading(false));

        return { success: true, message: "Invoice rejected" };
    } catch (error: any) {
        console.error("Error rejecting invoice:", error);
        dispatch(setError(error.message || "Error rejecting invoice"));
        throw error;
    }
};

export const submitInvoiceForApproval = (transactionLineId: string | number) => async (dispatch) => {
    try {
        dispatch(setApprovalActionLoading(true));
        const result = await submitForApproval(transactionLineId as any);

        dispatch(setApprovalStatus({
            status: result.approvalStatus,
            isApprover: result.isApprover,
            needsApproval: result.needsApproval,
            approvalProgress: result.approvalProgress || null,
        }));
        dispatch(setApprovalActionLoading(false));

        return { success: true, message: "Invoice submitted for approval" };
    } catch (error: any) {
        console.error("Error submitting for approval:", error);
        dispatch(setError(error.message || "Error submitting for approval"));
        throw error;
    }
};

export default slice.reducer; 